import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { Button } from "@nexus/ui";

/**
 * Dashboard-wide expand/collapse-all, alongside each widget's own
 * collapse/expand (`WidgetCollapseContext` in `@nexus/widget-sdk`, driven by
 * `instance.config.collapsible`/`defaultCollapsed` — set per widget in the
 * Dashboard editor's Display tab). A widget only participates once it
 * registers itself as collapsible; a dashboard with no collapsible widget
 * shows no "Collapse all" control at all.
 *
 * Ported from the pre-migration hardcoded report page's `CollapseContext`
 * (`webui/.../measurement/components/CollapseContext.tsx`) — same
 * expand-token/collapse-token mechanism, generalized to any dashboard
 * widget instead of one page's fixed sections.
 */
interface DashboardCollapseContextValue {
  register: (instanceId: string, collapsed: boolean) => void;
  unregister: (instanceId: string) => void;
  expandAllToken: number;
  collapseAllToken: number;
}

const DashboardCollapseContext = createContext<DashboardCollapseContextValue | null>(null);

interface DashboardCollapseController {
  hasCollapsible: boolean;
  allCollapsed: boolean;
  toggleAll: () => void;
}

const DashboardCollapseControllerContext = createContext<DashboardCollapseController | null>(null);

export function DashboardCollapseProvider({ children }: { children: ReactNode }) {
  const [states, setStates] = useState<Record<string, boolean>>({});
  const [expandAllToken, setExpandAllToken] = useState(0);
  const [collapseAllToken, setCollapseAllToken] = useState(0);

  const register = useCallback((instanceId: string, collapsed: boolean) => {
    setStates((current) =>
      current[instanceId] === collapsed ? current : { ...current, [instanceId]: collapsed },
    );
  }, []);

  const unregister = useCallback((instanceId: string) => {
    setStates((current) => {
      if (!(instanceId in current)) return current;
      const next = { ...current };
      delete next[instanceId];
      return next;
    });
  }, []);

  const value = useMemo<DashboardCollapseContextValue>(
    () => ({ register, unregister, expandAllToken, collapseAllToken }),
    [register, unregister, expandAllToken, collapseAllToken],
  );

  const entries = Object.values(states);
  const hasCollapsible = entries.length > 0;
  const allCollapsed = hasCollapsible && entries.every(Boolean);

  const controller = useMemo<DashboardCollapseController>(
    () => ({
      hasCollapsible,
      allCollapsed,
      toggleAll: () => {
        if (allCollapsed) setExpandAllToken((token) => token + 1);
        else setCollapseAllToken((token) => token + 1);
      },
    }),
    [hasCollapsible, allCollapsed],
  );

  return (
    <DashboardCollapseControllerContext.Provider value={controller}>
      <DashboardCollapseContext.Provider value={value}>
        {children}
      </DashboardCollapseContext.Provider>
    </DashboardCollapseControllerContext.Provider>
  );
}

/**
 * One widget instance's collapsed state. Behaves as plain local state when no
 * `DashboardCollapseProvider` is above it (e.g. an isolated widget preview) — only a
 * `collapsible` widget registers with the provider, so a dashboard made only
 * of non-collapsible widgets never shows the "Collapse all" button.
 */
export function useWidgetCollapseState(
  instanceId: string,
  collapsible: boolean,
  defaultCollapsed: boolean,
): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const ctx = useContext(DashboardCollapseContext);
  const expandToken = ctx?.expandAllToken ?? 0;
  const collapseToken = ctx?.collapseAllToken ?? 0;

  useEffect(() => {
    if (!collapsible || !ctx) return undefined;
    ctx.register(instanceId, collapsed);
    return () => ctx.unregister(instanceId);
    // Only re-register on identity/eligibility change; `collapsed` is synced by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsible, ctx, instanceId]);

  useEffect(() => {
    if (collapsible && ctx) ctx.register(instanceId, collapsed);
  }, [collapsible, ctx, instanceId, collapsed]);

  useEffect(() => {
    if (expandToken === 0) return;
    setCollapsed(false);
  }, [expandToken]);

  useEffect(() => {
    if (collapseToken === 0) return;
    setCollapsed(true);
  }, [collapseToken]);

  const toggle = useCallback(() => setCollapsed((value) => !value), []);
  return [collapsed, toggle];
}

/** Lets the page offer a collapse command only after a collapsible widget registers. */
export function useDashboardCollapseController() {
  return useContext(DashboardCollapseControllerContext);
}

export function DashboardCollapseButton() {
  const collapse = useDashboardCollapseController();
  if (!collapse?.hasCollapsible) return null;
  const label = collapse.allCollapsed ? "Expand all" : "Collapse all";
  const Icon = collapse.allCollapsed ? ChevronsUpDown : ChevronsDownUp;
  return (
    <Button
      variant="secondary"
      size="lg"
      className="min-w-36 whitespace-nowrap"
      onClick={collapse.toggleAll}
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      {label}
    </Button>
  );
}
